import create from "zustand";

import pushToAnalytics from "../../../../utils/analytics";
import { consentKeys } from "../../../../utils/consentHelper";
import { storageLocal, storageSession } from "../../../../utils/StorageHandler";
import { isWebView } from "../../../../utils/webViewRedirect";
import {
    createOrder as apiCreateOrder,
    fetchCountries as apiFetchCountries,
    fetchPaymentMethods as apiFetchPaymentMethods,
    fetchSalutations as apiFetchSalutations,
    fetchShippingMethods as apiFetchShippingMethods,
    updateContextOptions,
} from "../api";
import {
    iShopwareCalculatedPrice,
    iShopwareShippingMethod,
} from "../ShoppingCart/store";

export enum CheckoutStepEnum {
    PAYMENT_METHOD = 1,
    DELIVERY_FORM = 2,
    SUMMARY = 3,
}

export enum PaymentMethods {
    "PayPal" = "PayPal",
    "Rechnung" = "Rechnung",
    "Vorkasse" = "Vorkasse",
}

export const technicPaymentMethods = ["Rechnung"];

export interface iShopwareSalutation {
    id: string;
    salutationKey: string;
    displayName: string;
    letterName: string;
    createdAt: string;
    updatedAt: string | null;
    apiAlias: string;
}

export interface iShopwareCountry {
    id: string;
    name: string;
    iso: string;
    active: boolean;
    createdAt: string;
    updatedAt: string | null;
    apiAlias: string;
}

export interface iShopwarePaymentMethod {
    id: string;
    name: string;
    shortName: string;
    description: string;
    active: boolean;
    createdAt: string;
    updatedAt: string | null;
    apiAlias: string;
}

interface iPaymentResponse {
    redirectUrl: string | null;
    apiAlias: string;
}

export interface iCheckoutResponse {
    token: string;
    order: iShopwareOrder;
    payment: iPaymentResponse;
}

export interface iShopwareOrder {
    id: string;
    orderNumber: string;
    createdAt: string;
    updatedAt: string | null;
    apiAlias: string;
    price: iShopwareCalculatedPrice;
}

interface iCheckoutBillingAddress {
    company: string | null;
    zipcode: string;
    city: string;
    street: string;
    houseNumber: string;
    countryId: string;
    phoneNumber: string;
}

interface iCheckoutShippingAddress extends iCheckoutBillingAddress {
    salutationId: string | null;
    firstName: string;
    lastName: string;
}

export interface iCheckoutForm {
    email: string;
    salutationId: string | null;
    firstName: string;
    lastName: string;
    vatId: string;
    acceptedTermsOfService: boolean;
    acceptedNewsletter: boolean;
    hasShippingAddress: boolean;
    billingAddress: iCheckoutBillingAddress;
    shippingAddress?: iCheckoutShippingAddress;
    isWebview: boolean;
}

interface iCheckoutStore {
    isOrderSubmitting: boolean;
    currentStep: number;
    isPaymentMethodsLoading: boolean;
    paymentMethods: iShopwarePaymentMethod[];
    shippingMethods: iShopwareShippingMethod[];
    salutations: iShopwareSalutation[];
    countries: iShopwareCountry[];
    selectedPaymentMethod?: iShopwarePaymentMethod;
    backendValidationErrors: null | any;
    form: iCheckoutForm;
    order: iShopwareOrder | null;

    setForm(form: iCheckoutForm): void;
    setCurrentStep(step: number): void;
    fetchPaymentMethods(exclude?: PaymentMethods[]): void;
    fetchShippingMethods(): void;
    getSalutationById(id?: string | null): string;
    setSelectedPaymentMethod(paymentMethodId?: iShopwarePaymentMethod): void;
    fetchSalutations(): void;
    fetchCountries(): void;
    updatePaymentMethod(
        paymentMethodId: string | undefined
    ): Promise<iShopwarePaymentMethod | undefined>;
    createOrder(form: iCheckoutForm): Promise<iCheckoutResponse>;
}

const useCheckoutStore = create<iCheckoutStore>((set, get) => ({
    isOrderSubmitting: false,
    currentStep: CheckoutStepEnum.PAYMENT_METHOD,
    paymentMethods: [],
    shippingMethods: [],
    salutations: [],
    countries: [],
    isPaymentMethodsLoading: false,
    selectedPaymentMethod: undefined,
    order: null,
    backendValidationErrors: null,
    form: {
        email: "",
        salutationId: "",
        firstName: "",
        lastName: "",
        acceptedTermsOfService: false,
        acceptedNewsletter: false,
        hasShippingAddress: false,
        vatId: "",
        billingAddress: {
            countryId: "",
            company: "",
            zipcode: "",
            city: "",
            street: "",
            houseNumber: "",
            phoneNumber: "",
        },
        shippingAddress: {
            countryId: "",
            company: "",
            salutationId: "",
            firstName: "",
            lastName: "",
            zipcode: "",
            city: "",
            street: "",
            houseNumber: "",
            phoneNumber: "",
        },
        isWebview: isWebView(),
    },

    setForm: (form) => set({ form: form }),
    setCurrentStep: (step) => set({ currentStep: step }),
    setSelectedPaymentMethod: (paymentMethod) => {
        set({ selectedPaymentMethod: paymentMethod });
    },
    fetchPaymentMethods: async (exclude = []) => {
        set({ isPaymentMethodsLoading: true });
        const res = await apiFetchPaymentMethods();
        const paymentMethods = res.elements;
        if (exclude.length > 0) {
            const filteredPaymentMethods = paymentMethods.filter(
                (element) => !exclude.includes(element.name)
            );
            set({
                paymentMethods: filteredPaymentMethods,
                isPaymentMethodsLoading: false,
                // Set the first payment-method as Default
                selectedPaymentMethod: filteredPaymentMethods[0] ?? undefined,
            });
        } else {
            set({
                paymentMethods: paymentMethods,
                isPaymentMethodsLoading: false,
                // Set the first payment-method as Default
                selectedPaymentMethod: paymentMethods[0] ?? undefined,
            });
        }
        //Set the default Payment Method in Shopware Context
        const { selectedPaymentMethod } = get();
        if (selectedPaymentMethod) {
            void updateContextOptions({
                paymentMethodId: selectedPaymentMethod.id,
            });
        }
    },
    fetchSalutations: async () => {
        const res = await apiFetchSalutations();
        set({
            salutations: res.elements,
        });
    },
    fetchCountries: async () => {
        const res = await apiFetchCountries();
        set({
            countries: res.elements,
        });
    },
    fetchShippingMethods: async () => {
        const res = await apiFetchShippingMethods();
        set({
            shippingMethods: res.elements,
        });
    },

    //Used to display the address recipient in the summary
    getSalutationById: (id) => {
        if (!id) {
            return "";
        }
        const salutation = get().salutations.find(
            (salutation) => salutation.id === id
        );

        if (
            salutation?.displayName === "Herr" ||
            salutation?.displayName === "Frau"
        ) {
            return salutation.displayName;
        }
        //Don't return the shopware salutation when `keine Angabe` or any other is selected.
        return "";
    },

    updatePaymentMethod: async (paymentMethodId) => {
        const selected = get().paymentMethods.find((payment) => {
            return payment.id === paymentMethodId;
        });
        if (!selected) {
            return undefined;
        }
        try {
            set({ selectedPaymentMethod: selected });
            await updateContextOptions({
                paymentMethodId: selected.id,
            });
            return selected;
        } catch (error) {
            set({ selectedPaymentMethod: undefined });
            return undefined;
        }
    },

    createOrder: async (form) => {
        set({ isOrderSubmitting: true, backendValidationErrors: null });
        try {
            const meta = {
                urlParams: storageSession.get("app:parameter"),
                referrer: storageSession.get("app:referrer"),
                consentAnalyse: storageLocal.get(consentKeys.analytics),
                consentMarketing: storageLocal.get(consentKeys.marketing),
            };

            const body = {
                ...form,
                meta,
            };

            const data = await apiCreateOrder(body);
            if (data.order) {
                set({ order: data.order, isOrderSubmitting: false });
                pushToAnalytics("shop:order", {
                    orderNumber: get().order?.orderNumber,
                    cartValue: get().order?.price?.totalPrice,
                });
            }
            return data;
        } catch (error: any) {
            if (error.errors) {
                set({
                    backendValidationErrors: error.errors,
                });
            }
            set({ isOrderSubmitting: false });
            return error;
        }
    },
}));

export default useCheckoutStore;
